import { RentalDetailed } from './RentalDetailed';
import api from 'api';
import { get } from 'utils/fetch';
import { useState, useEffect, useContext } from 'react';
import { message, Spin, Input, Button } from 'antd';
import { GlobalContext } from 'GlobalContext';
import { isAdmin, isSale, isPhoneSize } from 'utils/util';
import { USDollar, parsePgArray } from 'utils/format';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';

function TableHeader() {
    return (
        <div
            style={{
                display: 'flex',
                borderBottom: '1px solid black',
            }}
        >
            <HeaderCell width={60}>Edit</HeaderCell>
            <HeaderCell width={140}>Rental Status</HeaderCell>
            <HeaderCell width={200}>Monthly Rent</HeaderCell>
            <HeaderCell width={200}>Lease Start Date</HeaderCell>
            <HeaderCell width={180}>Lease Expire Date</HeaderCell>
        </div>
    );
}

function HeaderCell({ width, children }) {
    return (
        <div
            style={{
                width: `${width}px`,
                minWidth: `${width}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                borderRight: '1px solid #ddd',
                flexShrink: 0,
            }}
        >
            {children}
        </div>
    );
}

function Th({ width, children }) {
    return (
        <th
            style={{
                width,
                minWidth: width,
                padding: '8px',
                borderRight: '1px solid #ddd',
                textAlign: 'center',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                background: '#f9f9f9',
            }}
        >
            {children}
        </th>
    );
}

function Td({ width, children }) {
    return (
        <td
            style={{
                width,
                minWidth: width,
                padding: '8px',
                borderRight: '1px solid #eee',
                textAlign: 'center',
                whiteSpace: 'nowrap',
            }}
        >
            {children}
        </td>
    );
}

function RentalTable({ rentals }) {
    const { assetManager } = useContext(GlobalContext);
    const { setRentalId } = assetManager;

    return (
        <div
            style={{
                overflow: 'auto',
                maxHeight: 'calc(100vh - 80px)',
                border: '1px solid #ddd',
                width: '100%',
            }}
        >
            <table
                style={{
                    borderCollapse: 'collapse',
                    width: 'max-content',
                    minWidth: '100%',
                }}
            >
                {/* Sticky header */}
                <thead
                    style={{
                        position: 'sticky',
                        top: 0,
                        background: 'white',
                        zIndex: 2,
                        borderBottom: '2px solid black',
                    }}
                >
                    <tr>
                        <Th width={60}>Edit</Th>
                        <Th width={140}>Rental Status</Th>
                        <Th width={200}>Monthly Rent</Th>
                        <Th width={200}>Lease Start Date</Th>
                        <Th width={180}>Lease Expire Date</Th>
                    </tr>
                </thead>

                <tbody>
                    {rentals
                        .sort((a, b) => a.id - b.id)
                        .map((rental) => (
                            <tr key={rental.id}>
                                <Td width={60}>
                                    <Button
                                        type="link"
                                        icon={<EditOutlined />}
                                        onClick={() => setRentalId(rental.id)}
                                    />
                                </Td>
                                <Td width={140}>
                                    {parsePgArray(rental.rental_status).join(
                                        ', '
                                    )}
                                </Td>
                                <Td width={200}>
                                    {USDollar.format(rental.monthly_rent)}
                                </Td>
                                <Td width={200}>
                                    {rental.lease_start_date &&
                                        new Date(
                                            rental.lease_start_date
                                        ).toLocaleDateString()}
                                </Td>
                                <Td width={180}>
                                    {rental.lease_expire_date &&
                                        new Date(
                                            rental.lease_expire_date
                                        ).toLocaleDateString()}
                                </Td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );
}

export function RentalInfo() {
    const {
        manageAssetId,
        setManageAssetId,
        accessToken,
        assetManager,
        refreshSession,
    } = useContext(GlobalContext);
    const { rentalId, setRentalId } = assetManager;
    const [rentals, setRentals] = useState([]);
    const [loading, setLoading] = useState(
        manageAssetId < 0 || rentalId !== 0 ? false : true
    );
    const [messageApi, contextHolder] = message.useMessage();

    const displayErrorMessage = (message) => {
        messageApi.open({
            type: 'error',
            content: message,
        });
    };

    async function fetchRental(assetId) {
        await get(
            api.asset.getRentals(assetId),
            setRentals,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        setLoading(false);
        refreshSession();
    }

    useEffect(() => {
        if (rentalId === 0 && manageAssetId > 0) {
            setLoading(true);
            fetchRental(manageAssetId);
        }
    }, [manageAssetId, rentalId]);

    return (
        <>
            {loading && (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <Spin />
                </div>
            )}

            {!loading && (
                <>
                    {rentalId !== 0 && (
                        <div
                            style={
                                isPhoneSize()
                                    ? {
                                          height: 'calc(100vh - 310px)',
                                          overflowY: 'scroll',
                                          scrollbarWidth: 'none',
                                          overflowX: 'hidden',
                                      }
                                    : {
                                          height: 'calc(100vh - 30px)',
                                          overflowY: 'scroll',
                                          scrollbarWidth: 'none',
                                          overflowX: 'hidden',
                                      }
                            }
                        >
                            <RentalDetailed />
                        </div>
                    )}
                    {rentalId === 0 && (
                        <>
                            {isAdmin() && (
                                <div style={{ marginTop: '10px' }}>
                                    <Button
                                        type="default"
                                        icon={<PlusOutlined />}
                                        onClick={() => setRentalId(-1)}
                                    >
                                        Add
                                    </Button>
                                </div>
                            )}
                            <RentalTable rentals={rentals} />
                        </>
                    )}
                </>
            )}
        </>
    );
}
